import React from "react";
import { Download, FileText, Sparkles } from "lucide-react";
import { Card } from "../../components/primitives";
import { EmptyState } from "../../components/DataTable";
import { SERVER_URL } from "../../../../utils/serverConfig";
import { useDashTheme } from "../../hooks/useDashTheme";
import { downloadHtmlAsPdf } from "./exportPdf";

function Bar({ w, h = 10, r = 6, delay = 0 }) {
  return (
    <div
      className="ov-sk"
      style={{
        width: w, height: h, borderRadius: r,
        animationDelay: `${delay}ms`,
      }}
    />
  );
}

function PaperShimmer({ t }) {
  return (
    <div style={{
      borderRadius: 16, padding: "28px 12px 40px",
      background: t.isDark ? t.c.surfaceMuted : "#D8DEE8",
      border: `1px solid ${t.c.border}`,
    }}>
      <style>{`
        @keyframes ov-shimmer {
          0% { background-position: 180% 0 }
          100% { background-position: -80% 0 }
        }
        .ov-sk {
          background-color: ${t.isDark ? "#2a2d33" : "#E6E9F0"};
          background-image: linear-gradient(105deg,
            transparent 0%,
            ${t.isDark ? "rgba(255,255,255,.06)" : "rgba(255,255,255,.85)"} 46%,
            transparent 70%);
          background-size: 220% 100%;
          animation: ov-shimmer 1.35s ease-in-out infinite;
        }
      `}</style>
      <div style={{
        maxWidth: 720, margin: "0 auto", background: t.c.surface,
        borderRadius: 2, padding: "28px 36px 36px",
        boxShadow: "0 22px 56px rgba(20,24,40,.14)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
          <Bar w={168} h={32} r={4} />
          <Bar w={140} h={10} delay={80} />
        </div>
        <Bar w={260} h={26} r={5} delay={40} />
        <div style={{ height: 10 }} />
        <Bar w={320} h={12} delay={90} />
        <div style={{ height: 6 }} />
        <Bar w={240} h={12} delay={120} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, margin: "22px 0 8px" }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{
              border: `1px solid ${t.c.border}`, borderRadius: 6, padding: "12px 10px",
            }}>
              <Bar w="46%" h={8} delay={i * 70} />
              <div style={{ height: 10 }} />
              <Bar w="68%" h={20} r={5} delay={40 + i * 70} />
              <div style={{ height: 8 }} />
              <Bar w="80%" h={8} delay={80 + i * 70} />
            </div>
          ))}
        </div>
        <div style={{ height: 18 }} />
        <Bar w={180} h={14} delay={160} />
        <div style={{ height: 12 }} />
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            <Bar w={72} h={12} delay={180 + i * 60} />
            <Bar w={72} h={12} delay={200 + i * 60} />
            <Bar w={`${70 - i * 8}%`} h={12} delay={220 + i * 60} />
            <Bar w={40} h={12} delay={240 + i * 60} />
          </div>
        ))}
        <div style={{ height: 16 }} />
        <Bar w={140} h={14} delay={280} />
        <div style={{ height: 12 }} />
        <Bar w="100%" h={11} delay={300} />
        <div style={{ height: 8 }} />
        <Bar w="94%" h={11} delay={330} />
        <div style={{ height: 8 }} />
        <Bar w="72%" h={11} delay={360} />
        <div style={{
          marginTop: 22, padding: 14, border: `1px solid ${t.c.border}`, borderRadius: 6,
        }}>
          <Bar w={90} h={16} r={4} delay={380} />
          <div style={{ height: 12 }} />
          <Bar w="88%" h={11} delay={400} />
          <div style={{ height: 8 }} />
          <Bar w="64%" h={11} delay={430} />
        </div>
        <div style={{ marginTop: 18, fontSize: 12.5, color: t.c.textTertiary, fontWeight: 550 }}>
          Writing the overview…
        </div>
      </div>
    </div>
  );
}

function downloadHtml(html, name) {
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export function DayOverview({ member, date, source, personName, onHours }) {
  const t = useDashTheme();
  const [loading, setLoading] = React.useState(false);
  const [pdfing, setPdfing] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [report, setReport] = React.useState(null);
  const frameRef = React.useRef(null);

  const run = React.useCallback(async () => {
    if (!member) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${SERVER_URL}/api/v1/work-summary-new/day-overview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId: member, date, source, force: true }),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.message || "Could not build this overview.");
      setReport(json.data);
      onHours?.(json.data?.empty ? null : json.data?.facts || null);
    } catch (e) {
      setError(e.message || "Could not build this overview.");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [member, date, source, onHours]);

  React.useEffect(() => {
    setReport(null);
    setError(null);
    onHours?.(null);
  }, [member, date, onHours]);

  const fileBase = `daily-overview-${String(personName || report?.person?.name || "member").replace(/[^\w.-]+/g, "_")}-${date}`;

  const fitFrame = () => {
    const frame = frameRef.current;
    const doc = frame && frame.contentDocument;
    if (!doc || !doc.body) return;
    const h = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
    frame.style.height = `${Math.max(h + 16, 520)}px`;
  };

  const savePdf = async () => {
    if (!report?.html || pdfing) return;
    setPdfing(true);
    try { await downloadHtmlAsPdf(report.html, `${fileBase}.pdf`); }
    catch (e) { setError(e.message || "Could not build the PDF."); }
    finally { setPdfing(false); }
  };

  const btn = (primary, extra = {}) => ({
    display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 650,
    padding: "8px 14px", borderRadius: 8, cursor: extra.disabled ? "not-allowed" : "pointer",
    opacity: extra.disabled ? 0.55 : 1,
    border: `1px solid ${primary ? t.c.primary : t.c.border}`,
    background: primary ? t.c.primary : t.c.surface,
    color: primary ? "#fff" : t.c.text,
  });

  const who = report?.person?.name || personName || "this person";
  const when = report?.dateLabel || date;

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <Card style={{ padding: "16px 18px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{
              fontSize: 10.5, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase",
              color: t.c.primary, marginBottom: 4,
            }}>Client report</div>
            <div style={{ fontWeight: 720, fontSize: 16.5, letterSpacing: "-0.02em" }}>Daily work overview</div>
            <div style={{ fontSize: 13, color: t.c.textSecondary, marginTop: 4, lineHeight: 1.45 }}>
              {member
                ? `${who} · ${when}. Generate a print-ready page, then download the PDF.`
                : "Pick a team member, then generate their overview."}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button type="button" className="st-reset st-focusable" disabled={!member || loading} onClick={run} style={btn(!report, { disabled: !member || loading })}>
              <Sparkles size={14} /> {loading ? "Writing…" : report ? "Regenerate" : "Generate"}
            </button>
            {report?.html && !report.empty && (
              <>
                <button type="button" className="st-reset st-focusable" disabled={pdfing} onClick={savePdf} style={btn(true, { disabled: pdfing })}>
                  <Download size={14} /> {pdfing ? "Building PDF…" : "Download PDF"}
                </button>
                <button type="button" className="st-reset st-focusable" onClick={() => downloadHtml(report.html, `${fileBase}.html`)} style={btn(false)}>
                  <FileText size={14} /> HTML
                </button>
              </>
            )}
          </div>
        </div>
      </Card>

      {!member && (
        <Card><EmptyState title="Choose someone" description="Pick a team member, then generate their overview." /></Card>
      )}
      {error && (
        <Card><EmptyState title="Could not finish" description={error} /></Card>
      )}
      {report?.empty && (
        <Card><EmptyState title="Nothing tracked on this day" description={report.message} /></Card>
      )}
      {loading && <PaperShimmer t={t} />}
      {!loading && report?.html && !report.empty && (
        <div style={{
          borderRadius: 16,
          padding: "28px 12px 40px",
          background: t.isDark ? t.c.surfaceMuted : "#D8DEE8",
          border: `1px solid ${t.c.border}`,
        }}>
          <div style={{
            maxWidth: 794, margin: "0 auto",
            background: "#fff",
            boxShadow: "0 22px 56px rgba(20,24,40,.16)",
            borderRadius: 2,
            overflow: "hidden",
          }}>
            <iframe
              ref={frameRef}
              title="Daily overview"
              srcDoc={report.html}
              onLoad={fitFrame}
              style={{ width: "100%", minHeight: 640, border: 0, background: "#fff", display: "block" }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
